'use client'

import { useStoreI18n } from '@/store'
import { Typo3SectionSpotSanityReports } from '@/types/typo3'
import { Accordion, Container, Section } from '@/ui/components'
import { useSearchParams } from 'next/navigation'
import { Tabs } from 'radix-ui'
import { memo } from 'react'
import ReportsAccordion from './Components/ReportsAccordion'
import ReportsAccordionHead from './Components/ReportsAccordionHead'
import IcerinkAccordionHead from './Components/Templates/Icerink/AccordionHead'
import LiftsAccordionBody from './Components/Templates/Lifts/AccordionBody'
import SlopesAccordionBody from './Components/Templates/Slopes/AccordionBody'
import SnowreportTable from './Components/Templates/Snowreport/Table'
import SnowshoeAccordionHead from './Components/Templates/Snowshoe/AccordionHead'
import TrailsAccordionBody from './Components/Templates/Trails/AccordionBody'
import WinterhikeAccordionBody from './Components/Templates/Winterhike/AccordionBody'
import WinterhikeAccordionHead from './Components/Templates/Winterhike/AccordionHead'

import Image from 'next/image'
import InfoComponent from './Components/Info'
import TabsTrigger from './Components/Tabs'

interface Props {
  data: Typo3SectionSpotSanityReports
}

const SpotSanityReports = ({ data }: Props) => {
  const c = data.content
  const D = useStoreI18n.use.dictionary()
  const searchParams = useSearchParams()
  const tabParam = searchParams.get('tab')

  const defaultTab =
    tabParam || data.content.activeTab || data.content.tabs?.[0]

  return (
    <Section data={data} className={'s-spot-sanity-reports'}>
      <Tabs.Root defaultValue={defaultTab}>
        <TabsTrigger tabs={c.tabs} items={c.items} dictionary={D} />
        <Container anim="fade-in" className={''}>
          {c.tabs.includes('lifts') && c.items.liftsAndSlopes && (
            <Tabs.Content value={'lifts'}>
              <div className={''}>
                {c?.itemsInfo?.liftsAndSlopes?.hasGeneralInformation ===
                  true && (
                  <InfoComponent
                    data={c?.itemsInfo?.liftsAndSlopes?.generalInformation}
                  />
                )}
                <ReportsAccordion
                  label={D['fe.lift.railways']}
                  sidenav={c.items.liftsAndSlopes.map((item) => ({
                    label: item?.titleMountain || '',
                    id: 'lift-' + item?.facetId
                  }))}
                >
                  {c.items.liftsAndSlopes.map((item, i) => (
                    <Accordion.Item
                      value={'liftsAndSlopes-' + i.toString()}
                      key={i?.toString()}
                      contentClassName={'pb-lg!'}
                      id={`lift-${item?.facetId}`}
                      data-scroll-spy={`lift-${item?.facetId}`}
                      head={
                        <ReportsAccordionHead
                          title={item?.titleMountain || ''}
                          status={item?.status || 'CLOSED'}
                          items={[
                            {
                              label: D['fe.lift.railways'],
                              open: item?.numberLiftsOpen || 0,
                              total: item?.numberLifts || 0
                            }
                          ]}
                        />
                      }
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      <LiftsAccordionBody
                        isLayoutWinter={data.plugin.isLayoutWinter}
                        {...item}
                      />
                    </Accordion.Item>
                  ))}
                </ReportsAccordion>
              </div>
            </Tabs.Content>
          )}
          {data.content.tabs.includes('slopes') && c.items.liftsAndSlopes && (
            <Tabs.Content value={'slopes'}>
              <div className={''}>
                {c?.itemsInfo?.liftsAndSlopes?.hasGeneralInformation ===
                  true && (
                  <InfoComponent
                    data={c?.itemsInfo?.liftsAndSlopes?.generalInformation}
                  />
                )}

                <ReportsAccordion
                  label={D['fe.reports.tab.slopes']}
                  sidenav={c.items.liftsAndSlopes.map((item) => ({
                    label: item?.titleMountain || '',
                    id: 'slopes-' + item?.facetId
                  }))}
                >
                  {c.items.liftsAndSlopes.map((item, i) => (
                    <Accordion.Item
                      value={'liftsAndSlopes-' + i.toString()}
                      key={i?.toString()}
                      id={`slopes-${item?.facetId}`}
                      head={
                        <ReportsAccordionHead
                          title={item?.titleMountain || ''}
                          status={item?.status || 'CLOSED'}
                          items={[
                            {
                              label: D['fe.reports.tab.slopes'],
                              open: item?.numberSlopesOpen || 0,
                              total: item?.numberSlopes || 0
                            }
                          ]}
                        />
                      }
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      <SlopesAccordionBody
                        isLayoutWinter={data.plugin.isLayoutWinter}
                        {...item}
                      />
                    </Accordion.Item>
                  ))}
                </ReportsAccordion>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('trails') && c.items.trails && (
            <Tabs.Content value={'trails'}>
              <div className={''}>
                {c?.itemsInfo?.trails?.hasGeneralInformation === true && (
                  <InfoComponent
                    data={c?.itemsInfo?.trails?.generalInformation}
                  />
                )}
                <ReportsAccordion
                  label={D['fe.reports.tab.trails']}
                  sidenav={c.items.trails.map((item) => ({
                    label: item?.title || '',
                    id: 'trails-' + item?.uid
                  }))}
                >
                  {c.items.trails.map((item, i) => (
                    <Accordion.Item
                      value={'trails-' + i.toString()}
                      key={i?.toString()}
                      contentClassName={'pb-lg!'}
                      id={`trails-${item?.uid}`}
                      head={
                        <ReportsAccordionHead
                          title={item?.title || ''}
                          status={item?.status || 'CLOSED'}
                          items={[
                            {
                              label: D['fe.trails.skating'],
                              open: item?.totalLengthKmPreparedSkating || 0,
                              total: item?.totalLengthKmSkating || 0,
                              append: D['fe.trails.kmOpen']
                            },
                            {
                              label: D['fe.trails.classic'],
                              open: item?.totalLengthKmPreparedClassic || 0,
                              total: item?.totalLengthKmClassic || 0,
                              append: D['fe.trails.kmOpen']
                            }
                          ]}
                        />
                      }
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      <TrailsAccordionBody
                        isLayoutWinter={data.plugin.isLayoutWinter}
                        {...item}
                      />
                    </Accordion.Item>
                  ))}
                </ReportsAccordion>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('winterhike') && c.items.winterHike && (
            <Tabs.Content value={'winterhike'}>
              <div className={''}>
                {c?.itemsInfo?.winterHike?.hasGeneralInformation === true && (
                  <InfoComponent
                    data={c?.itemsInfo?.winterHike?.generalInformation}
                  />
                )}

                <Accordion.Container>
                  {c.items.winterHike.map((item, i) => (
                    <Accordion.Item
                      value={'winterHike-' + i.toString()}
                      key={i?.toString()}
                      head={<WinterhikeAccordionHead {...item} />}
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      <WinterhikeAccordionBody
                        isLayoutWinter={data.plugin.isLayoutWinter}
                        {...item}
                      />
                    </Accordion.Item>
                  ))}
                </Accordion.Container>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('snowshoe') && c.items.snowshoe && (
            <Tabs.Content value={'snowshoe'}>
              <div className={''}>
                {c?.itemsInfo?.snowshoe?.hasGeneralInformation === true && (
                  <InfoComponent
                    data={c?.itemsInfo?.snowshoe?.generalInformation}
                  />
                )}
                <Accordion.Container>
                  {c.items.snowshoe.map((item, i) => (
                    <Accordion.Item
                      value={'snowshoe-' + i.toString()}
                      key={i?.toString()}
                      head={<SnowshoeAccordionHead {...item} />}
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      {/* <SnowshoeAccordionBody
                        isLayoutWinter={data.plugin.isLayoutWinter}
                        {...item}
                      /> */}
                      {''}
                    </Accordion.Item>
                  ))}
                </Accordion.Container>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('sledging') && c.items.sledging && (
            <Tabs.Content value={'sledging'}>
              <div className={''}>
                {c?.itemsInfo?.sledging?.hasGeneralInformation === true && (
                  <InfoComponent
                    data={c?.itemsInfo?.sledging?.generalInformation}
                  />
                )}
                <ReportsAccordion
                  label={D['fe.reports.tab.sledging']}
                  sidenav={c.items.sledging.map((item) => ({
                    label: item?.title || '',
                    id: 'sledging-' + item?.uid
                  }))}
                >
                  {c.items.sledging.map((item, i) => (
                    <Accordion.Item
                      value={'sledging-' + i.toString()}
                      key={i?.toString()}
                      contentClassName={'pb-lg!'}
                      id={`sledging-${item?.uid}`}
                      triggerClassName={'pointer-events-none [&_svg]:hidden'}
                      head={
                        <ReportsAccordionHead
                          pretitle={item?.designationLabel || ''}
                          title={item?.title || ''}
                          status={item?.status || 'CLOSED'}
                          items={[
                            {
                              label: D['fe.reports.tab.sledging'],
                              open: item?.openLengthKm || 0,
                              total: item?.lengthKm || 0,
                              append: D['fe.sledging.kmOpen']
                            }
                          ]}
                        />
                      }
                    />
                  ))}
                </ReportsAccordion>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('icerink') && c.items.iceRink && (
            <Tabs.Content value={'icerink'}>
              <div className={''}>
                {c?.itemsInfo?.iceRink?.hasGeneralInformation === true && (
                  <InfoComponent
                    data={c?.itemsInfo?.iceRink?.generalInformation}
                  />
                )}
                <Accordion.Container>
                  {c.items.iceRink.map((item, i) => (
                    <Accordion.Item
                      value={'iceRink-' + i.toString()}
                      key={i?.toString()}
                      head={<IcerinkAccordionHead {...item} />}
                      closeButton={{
                        text: D['fe.reports.tab.close']
                      }}
                    >
                      {''}
                    </Accordion.Item>
                  ))}
                </Accordion.Container>
              </div>
            </Tabs.Content>
          )}

          {data.content.tabs.includes('avalanche') && c?.items?.avalanche && (
            <Tabs.Content value={'avalanche'}>
              {c?.items?.avalanche?.linkMap && (
                <div className={''}>
                  <Image
                    alt={'Avalanche'}
                    src={c?.items?.avalanche?.linkMap}
                    width={1000}
                    height={1000}
                  />
                </div>
              )}
            </Tabs.Content>
          )}

          {data.content.tabs.includes('snowreport') && c.items.snowReport && (
            <Tabs.Content value={''}>
              <SnowreportTable {...c.items.snowReport} />
            </Tabs.Content>
          )}
        </Container>
      </Tabs.Root>
    </Section>
  )
}

export default memo(SpotSanityReports)
